import type { WalletPreviewData } from './WalletPreviewDialog';

type Payload = Record<string, unknown> | null | undefined;

const readString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

export const isRichSms = (payload: Payload): boolean =>
  payload?.is_rich_sms === 1 || payload?.is_rich_sms === true || payload?.enrichi === true;

export const readEmailSubject = (payload: Payload): string | undefined =>
  readString(payload?.object) ?? readString(payload?.emailSubject);

export const readSmsMessage = (payload: Payload): string => readString(payload?.message) ?? '';

interface RichSmsContent {
  mode?: 'unlayer' | 'html';
  html?: string;
  design?: object;
}

const readEditorMode = (value: unknown): RichSmsContent['mode'] =>
  value === 'unlayer' || value === 'html' ? value : undefined;

export const readRichSmsContent = (payload: Payload): RichSmsContent | null => {
  const rich = asRecord(payload?.richsms);
  const html = readString(rich?.richsmsHtml ?? payload?.landingPageHtml);
  const design = asRecord(rich?.richsmsDesign ?? payload?.landingPageDesign) ?? undefined;
  if (!html && !design) return null;
  return { mode: readEditorMode(rich?.richsmsEditorMode ?? payload?.landingPageEditorMode), html, design };
};

export const withRichSmsContent = (
  payload: Record<string, unknown> | null,
  content: { mode: 'unlayer' | 'html'; html: string; design?: object },
): Record<string, unknown> => ({
  ...payload,
  richsms: {
    ...asRecord(payload?.richsms),
    richsmsHtml: content.html,
    richsmsDesign: content.design ?? '',
    richsmsEditorMode: content.mode,
  },
});

export const withEmailContent = (
  payload: Record<string, unknown> | null,
  content: { mode: 'unlayer' | 'html'; html: string; design?: object; subject: string },
): Record<string, unknown> => ({
  ...payload,
  type: 'email',
  emailEditorMode: content.mode,
  emailHtml: content.html,
  object: content.subject,
  ...(content.design ? { emailDesign: content.design } : {}),
});

export const readWalletPreview = (payload: Payload): WalletPreviewData => {
  const versoBlocks = payload?.versoBlocks;
  return {
    title: readString(payload?.title) ?? '',
    message: readSmsMessage(payload),
    versoBlocks: Array.isArray(versoBlocks) ? (versoBlocks as WalletPreviewData['versoBlocks']) : [],
  };
};

export const couponIndicesIn = (message: string): Set<number> =>
  new Set(Array.from(message.matchAll(/#NUM_COUPON_(\d+)#/g), match => Number(match[1])));
